import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  House, ChatCircle, PlugsConnected, ShieldCheck, Brain,
  ListDashes, Calendar, Bell, Gear, SignOut,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/",              label: "Overview",       icon: House },
  { to: "/conversation",  label: "Conversation",   icon: ChatCircle },
  { to: "/approvals",     label: "Approvals",      icon: Bell,        badgeKey: "approvals" },
  { to: "/tasks",         label: "Scheduled",      icon: Calendar },
  { to: "/activity",      label: "Activity log",   icon: ListDashes },
  { to: "/memory",        label: "Memory",         icon: Brain },
  { to: "/integrations",  label: "Integrations",   icon: PlugsConnected },
  { to: "/trust",         label: "Trust boundaries", icon: ShieldCheck },
  { to: "/setup",         label: "Setup",          icon: Gear },
];

export default function DashboardLayout() {
  const location = useLocation();
  const { data: pendingApprovals = [] } = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => api.approvals.list("pending"),
    refetchInterval: 5000,
  });
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: api.profile.get });
  const { user, logout } = useAuth();

  const badges = { approvals: pendingApprovals.length };

  return (
    <div className="relative z-10 flex min-h-screen text-stone-100">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-stone-800 bg-[#0c0a09] flex flex-col" data-testid="sidebar">
        <div className="px-6 py-8 border-b border-stone-800">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500">Online</span>
          </div>
          <h1 className="font-serif text-3xl mt-2 tracking-tight" data-testid="app-title">Copilot</h1>
          <p className="text-xs text-stone-500 mt-1 font-mono">executive assistant · v1</p>
        </div>

        <nav className="flex-1 py-4">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-colors duration-150
                  ${active
                    ? "border-orange-600 bg-stone-900/60 text-stone-50"
                    : "border-transparent text-stone-400 hover:text-stone-100 hover:bg-stone-900/40"}`}
              >
                <Icon size={16} weight={active ? "duotone" : "regular"} />
                <span className="flex-1">{item.label}</span>
                {badgeCount > 0 && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 bg-orange-600 text-white">
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-stone-800 px-6 py-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Principal</div>
          <div className="mt-1 flex items-center gap-2">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-6 h-6 rounded-full" data-testid="user-avatar" />
            ) : (
              <div className="w-6 h-6 bg-stone-800 border border-stone-700 flex items-center justify-center text-[10px] text-stone-300 font-mono">
                {(profile?.name || profile?.email || "?").slice(0,1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-stone-200 truncate" data-testid="user-name">{profile?.name || user?.email || "—"}</div>
              <div className="text-[10px] text-stone-500 font-mono truncate">{profile?.email}</div>
            </div>
            <button data-testid="btn-logout" onClick={logout} title="Sign out"
              className="text-stone-500 hover:text-orange-500 transition-colors">
              <SignOut size={14} />
            </button>
          </div>
          <div className="mt-2 text-[10px] text-stone-500 font-mono">{profile?.timezone || "UTC"} · brief {profile?.briefing_time || "07:00"}</div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
